import { useEffect, useMemo, type CSSProperties } from 'react';
import type { ListingCard as ListingCardData } from '@dunea/shared';
import { classNames } from '~/utils/classNames';
import { CARD } from '~/components/workbench/tools/payments/ui';
import {
  cardPriceLabel,
  googleFontsHref,
  installsLabel,
  listingBadges,
  starStates,
  themeFontFamilies,
  themePreviewVars,
  type PreviewTokens,
} from './marketplace-model';

// Presentational pieces shared by the Marketplace panel views and the drawer.

export function Stars({ rating, size = 'text-[11px]' }: { rating: number; size?: string }) {
  return (
    <span className="inline-flex items-center gap-px" aria-label={`${Math.round(rating * 10) / 10} out of 5`}>
      {starStates(rating).map((filled, i) => (
        <span
          key={i}
          className={classNames(filled ? 'i-ph:star-fill text-warning' : 'i-ph:star text-faint', size)}
          aria-hidden
        />
      ))}
    </span>
  );
}

export function ListingIcon({ url, name, size = 36 }: { url: string | null; name: string; size?: number }) {
  return url ? (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      referrerPolicy="no-referrer"
      className="shrink-0 rounded-lg border border-hairline bg-sunken object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      className="flex shrink-0 items-center justify-center rounded-lg border border-hairline bg-sunken text-[13px] font-medium text-secondary"
      style={{ width: size, height: size }}
      aria-hidden
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}

export function VerifiedBadge() {
  return <span className="i-ph:seal-check-fill shrink-0 text-[12px] text-info" title="Verified developer" />;
}

export function DeveloperLine({ developer }: { developer: ListingCardData['developer'] }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-[11px] text-muted">
      <span className="truncate">{developer.displayName}</span>
      {developer.verified && <VerifiedBadge />}
    </span>
  );
}

const BADGE_TONES = {
  accent: 'border-accent/25 text-accent',
  info: 'border-info/25 text-info',
  success: 'border-success/25 text-success',
  warning: 'border-warning/25 text-warning',
  danger: 'border-danger/25 text-danger',
  muted: 'border-subtle text-muted',
} as const;

/** Verified / New / Featured / By Dunea. */
export function ListingBadges({ item }: { item: Pick<ListingCardData, 'badges' | 'featured'> }) {
  const badges = listingBadges(item);

  if (badges.length === 0) {
    return null;
  }

  return (
    <>
      {badges.map((b) => (
        <span key={b.id} title={b.title} className={classNames('ui-pill !py-0 !text-[10px]', BADGE_TONES[b.tone])}>
          {b.label}
        </span>
      ))}
    </>
  );
}

/** A catalog card. `badge` shows e.g. "Installed". */
export function ListingCard({
  item,
  onOpen,
  badge,
}: {
  item: ListingCardData;
  onOpen: () => void;
  badge?: string | null;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={classNames(
        CARD,
        'flex w-full items-start gap-3 p-3.5 text-left transition-colors duration-150 hover:border-subtle',
      )}
    >
      <ListingIcon url={item.iconUrl} name={item.name} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-[13px] font-medium text-primary">{item.name}</span>
          <span className="shrink-0 text-[11px] text-secondary">{cardPriceLabel(item)}</span>
        </div>
        <p className="line-clamp-2 text-[11.5px] leading-relaxed text-secondary">{item.tagline}</p>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <DeveloperLine developer={item.developer} />
          <span className="inline-flex items-center gap-1 text-[11px] text-muted">
            <Stars rating={item.rating} />
            <span className="[font-variant-numeric:tabular-nums]">{item.ratingCount}</span>
          </span>
          <span className="text-[11px] text-muted">{installsLabel(item.installCount)}</span>
          <ListingBadges item={item} />
          {badge && <span className="ui-pill border-success/25 text-success">{badge}</span>}
        </div>
      </div>
    </button>
  );
}

/** Adds one <link> per distinct Google Fonts URL to <head> (never removed: cards share them). */
export function useGoogleFonts(families: readonly string[]) {
  const key = families.join('|');
  const href = useMemo(() => googleFontsHref(key.split('|')), [key]);

  useEffect(() => {
    if (!href || typeof document === 'undefined') {
      return;
    }

    const id = `mkt-font-${href}`;

    if (document.getElementById(id)) {
      return;
    }

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }, [href]);
}

/** A mini mock page painted with the theme's colours, fonts and radius. */
export function ThemePreview({ tokens, name, tall }: { tokens: PreviewTokens; name: string; tall?: boolean }) {
  useGoogleFonts(themeFontFamilies(tokens));

  const vars = themePreviewVars(tokens) as CSSProperties;

  return (
    <div
      aria-hidden
      style={{
        ...vars,
        background: 'var(--t-bg)',
        color: 'var(--t-text)',
        fontFamily: 'var(--t-font-body)',
        borderColor: 'var(--t-border)',
      }}
      className={classNames('overflow-hidden rounded-lg border', tall ? 'h-[220px]' : 'h-[150px]')}
    >
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ borderBottom: '1px solid var(--t-border)' }}
      >
        <span style={{ fontFamily: 'var(--t-font-heading)', fontSize: 11, fontWeight: 600 }}>{name}</span>
        <span className="flex gap-2" style={{ fontSize: 8, color: 'var(--t-muted)' }}>
          <span>Shop</span>
          <span>About</span>
          <span>Contact</span>
        </span>
      </div>
      <div className="flex flex-col px-3 py-3" style={{ gap: 'var(--t-gap)' }}>
        <div>
          <div
            style={{
              fontFamily: 'var(--t-font-heading)',
              fontSize: 'var(--t-h1)',
              lineHeight: 1.05,
              fontWeight: 600,
              letterSpacing: '-0.01em',
            }}
          >
            Made with care.
          </div>
          <div style={{ marginTop: 4, fontSize: 9, color: 'var(--t-muted)' }}>Small batch goods, shipped weekly.</div>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            style={{
              background: 'var(--t-primary)',
              color: 'var(--t-primary-text)',
              borderRadius: 'var(--t-radius)',
              fontSize: 9,
              padding: '4px 9px',
              fontWeight: 600,
            }}
          >
            Shop now
          </span>
          <span
            style={{
              border: '1px solid var(--t-border)',
              borderRadius: 'var(--t-radius)',
              fontSize: 9,
              padding: '3px 8px',
            }}
          >
            Learn more
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                background: 'var(--t-surface)',
                border: '1px solid var(--t-border)',
                borderRadius: 'var(--t-radius)',
                padding: 5,
              }}
            >
              <div
                style={{
                  height: tall ? 34 : 18,
                  borderRadius: 'calc(var(--t-radius) / 2)',
                  background: i === 1 ? 'var(--t-accent)' : 'var(--t-border)',
                }}
              />
              <div
                style={{ marginTop: 4, fontFamily: 'var(--t-font-heading)', fontSize: 'var(--t-h2)', lineHeight: 1 }}
              >
                {['Aa', 'Bb', 'Cc'][i]}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Category chips with an "All" chip. */
export function CategoryChips({
  categories,
  value,
  onChange,
  label,
}: {
  categories: readonly (readonly [string, string])[];
  value: string;
  onChange: (next: string) => void;
  label: string;
}) {
  return (
    <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-0.5" role="group" aria-label={label}>
      {[['', 'All'] as const, ...categories].map(([v, text]) => (
        <button
          key={v || 'all'}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={classNames('ui-chip shrink-0', value === v && '!border-prominent !bg-sunken !text-primary')}
        >
          {text}
        </button>
      ))}
    </div>
  );
}
